import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, ChevronRight, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { MeetingRecordDetailModal } from "@/components/governance/MeetingRecordDetailModal";
import { ResolutionDetailModal } from "@/components/governance/ResolutionDetailModal";
import { CollapsibleRecordRow, CollapsibleRecordList } from "@/components/governance/CollapsibleRecordRow";
import { JournalEntryLogSection } from "@/components/governance/JournalEntryLogSection";
import { ResolutionRegistryList } from "@/components/governance/ResolutionRegistryList";

interface Props {
  unaId: string | undefined;
}

/**
 * Extracted "What happened" record surface from UNAGovernance.
 * Reuses the same modals and download logic. No MainLayout wrapping.
 */
export function GovernanceRecordContent({ unaId }: Props) {
  const navigate = useNavigate();
  const [selectedMeeting, setSelectedMeeting] = useState<any>(null);
  const [selectedResolution, setSelectedResolution] = useState<any>(null);
  const [downloadingResolutionId, setDownloadingResolutionId] = useState<string | null>(null);
  const [downloadingMeetingId, setDownloadingMeetingId] = useState<string | null>(null);

  const downloadFromDocRow = async (doc: any | null, label: string, fallbackFilename: string) => {
    if (!doc) {
      toast.error("We could not find that document.");
      return;
    }
    let url: string | null = doc?.signed_file_url || doc?.file_url || null;
    let filePath: string | null = doc?.file_path || null;
    const canPrepare =
      !!doc?.id && (!!doc?.custom_content || (doc?.document_type === "formal_meeting_minutes" && !!doc?.task_id));
    if (!url && !filePath && canPrepare) {
      toast.info(`${label} just needs its PDF prepared. Preparing now.`);
      const { regenerateDocumentPdf } = await import("@/lib/regeneratePdf");
      const res = await regenerateDocumentPdf(doc.id);
      if (res.error) {
        toast.error(res.error);
        return;
      }
      if (res.file_path) filePath = res.file_path;
    }
    if (!url && !filePath) {
      toast.error("There is no file to download yet.");
      return;
    }
    if (filePath) {
      const { data: signed } = await supabase.storage
        .from("documents")
        .createSignedUrl(filePath, 300, { download: fallbackFilename });
      url = signed?.signedUrl || url;
    }
    if (!url) {
      toast.error("We could not build a download link. Please try again.");
      return;
    }
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fallbackFilename;
    link.click();
    window.URL.revokeObjectURL(blobUrl);
  };

  const handleResolutionDownload = async (resolutionId: string, resolutionTitle: string) => {
    setDownloadingResolutionId(resolutionId);
    try {
      const { data: doc } = await supabase
        .from("generated_documents" as any)
        .select("id, file_url, signed_file_url, file_path, document_name, custom_content, document_type, task_id")
        .eq("resolution_id", resolutionId)
        .maybeSingle();
      await downloadFromDocRow(doc as any, "This resolution", `${resolutionTitle.replace(/[^a-z0-9]/gi, "-")}.pdf`);
    } catch {
      toast.error("That download did not go through. Please try again.");
    } finally {
      setDownloadingResolutionId(null);
    }
  };

  const handleMeetingDownload = async (meetingId: string, meetingTitle: string) => {
    setDownloadingMeetingId(meetingId);
    try {
      const { data: doc } = await supabase
        .from("generated_documents" as any)
        .select("id, file_url, signed_file_url, file_path, document_name, custom_content, document_type, task_id")
        .eq("meeting_id", meetingId)
        .maybeSingle();
      await downloadFromDocRow(doc as any, "This meeting document", `${meetingTitle.replace(/[^a-z0-9]/gi, "-")}.pdf`);
    } catch {
      toast.error("That download did not go through. Please try again.");
    } finally {
      setDownloadingMeetingId(null);
    }
  };

  const { data: recentMeetings = [], isLoading: meetingsLoading, isError: meetingsError } = useQuery({
    queryKey: ["gov-record-meetings", unaId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("meeting_minutes")
        .select(
          `id, meeting_type, meeting_date, meeting_purpose, meeting_location, meeting_start_time,
           status, task_id, actual_attendees, expected_attendees, special_guests, created_at`,
        )
        .eq("una_id", unaId!)
        .in("status", ["finalized", "prep", "archived", "reviewing"])
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!unaId,
  });

  const { data: resolutions = [], isLoading: resolutionsLoading, isError: resolutionsError } = useQuery({
    queryKey: ["gov-record-resolutions", unaId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("una_resolutions" as any)
        .select("*")
        .eq("una_id", unaId!)
        .order("resolution_date", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!unaId,
  });

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      Governance: "bg-blue-100 text-blue-800",
      Financial: "bg-green-100 text-green-800",
      "Asset Management": "bg-purple-100 text-purple-800",
      Membership: "bg-yellow-100 text-yellow-800",
      Operations: "bg-slate-100 text-slate-800",
      Policy: "bg-orange-100 text-orange-800",
      Appointment: "bg-red-100 text-red-800",
    };
    return colors[category] || "bg-gray-100 text-gray-800";
  };

  return (
    <div className="space-y-6">
      {/* Meeting Records */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              <CardTitle>Meeting Records</CardTitle>
              {recentMeetings.length > 0 && <Badge variant="secondary">{recentMeetings.length}</Badge>}
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate("/workflows?filter=meeting")}>
              Record New Meeting
            </Button>
          </div>
          <CardDescription>Certified minutes and meeting preparation.</CardDescription>
        </CardHeader>
        <CardContent>
          {meetingsLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : meetingsError ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              We could not load meetings just now. Please refresh in a moment.
            </p>
          ) : recentMeetings.length > 0 ? (
            <CollapsibleRecordList maxVisible={3}>
              {recentMeetings.map((meeting: any) => {
                const meetingTypeFormatted =
                  meeting.meeting_type?.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase()) || "Meeting";
                const attendeeCount =
                  meeting.status === "prep"
                    ? Array.isArray(meeting.expected_attendees)
                      ? meeting.expected_attendees.length
                      : 0
                    : Array.isArray(meeting.actual_attendees)
                      ? meeting.actual_attendees.length
                      : 0;
                return (
                  <CollapsibleRecordRow
                    key={meeting.id}
                    id={meeting.id}
                    title={meetingTypeFormatted}
                    date={meeting.created_at}
                    type="meeting"
                    badge={{
                      label: meeting.status === "prep" ? "Meeting Prep" : "Recorded",
                      className:
                        meeting.status === "prep" ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800",
                    }}
                    subtitle={`${attendeeCount} attendees • ${meeting.meeting_purpose || "Board Meeting"}`}
                    onClick={() => setSelectedMeeting(meeting)}
                    actions={
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMeetingDownload(meeting.id, meetingTypeFormatted);
                        }}
                        disabled={downloadingMeetingId === meeting.id}
                      >
                        {downloadingMeetingId === meeting.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </Button>
                    }
                    expandedContent={
                      <div className="space-y-2 text-sm">
                        {meeting.meeting_purpose && (
                          <p>
                            <span className="text-muted-foreground">Purpose:</span> {meeting.meeting_purpose}
                          </p>
                        )}
                        {meeting.meeting_location && (
                          <p>
                            <span className="text-muted-foreground">Location:</span> {meeting.meeting_location}
                          </p>
                        )}
                        <p>
                          <span className="text-muted-foreground">Attendees:</span> {attendeeCount}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMeeting(meeting);
                          }}
                        >
                          View Details
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    }
                  />
                );
              })}
            </CollapsibleRecordList>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">
              No meetings recorded yet. Your first meeting will appear here once it is minuted.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Resolutions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              <CardTitle>Resolutions and Actions</CardTitle>
              {resolutions.length > 0 && <Badge variant="secondary">{resolutions.length}</Badge>}
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate("/workflows?filter=resolution")}>
              New Resolution
            </Button>
          </div>
          <CardDescription>Official decisions and governance actions on record.</CardDescription>
        </CardHeader>
        <CardContent>
          {resolutionsLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : resolutionsError ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              We could not load resolutions just now. Please refresh in a moment.
            </p>
          ) : resolutions.length > 0 ? (
            <CollapsibleRecordList maxVisible={3}>
              {resolutions.map((resolution: any) => (
                <CollapsibleRecordRow
                  key={resolution.id}
                  id={resolution.id}
                  title={resolution.resolution_title}
                  date={resolution.resolution_date}
                  type="resolution"
                  badge={{
                    label: resolution.resolution_category,
                    className: getCategoryColor(resolution.resolution_category),
                  }}
                  subtitle={`${resolution.whereas_clauses?.length || 0} WHEREAS • ${resolution.resolved_clauses?.length || 0} RESOLVED`}
                  onClick={() => setSelectedResolution(resolution)}
                  actions={
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResolutionDownload(resolution.id, resolution.resolution_title);
                      }}
                      disabled={downloadingResolutionId === resolution.id}
                    >
                      {downloadingResolutionId === resolution.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                    </Button>
                  }
                  expandedContent={
                    <div className="space-y-2 text-sm">
                      <p>
                        <span className="text-muted-foreground">Adopted:</span>{" "}
                        {format(new Date(resolution.resolution_date), "MMMM d, yyyy")}
                      </p>
                      {resolution.filed_date && (
                        <p>
                          <span className="text-muted-foreground">Filed:</span>{" "}
                          {format(new Date(resolution.filed_date), "MMMM d, yyyy")}
                        </p>
                      )}
                      {resolution.implementation_notes && (
                        <p className="text-muted-foreground italic">
                          {resolution.implementation_notes.substring(0, 100)}
                          {resolution.implementation_notes.length > 100 ? "..." : ""}
                        </p>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedResolution(resolution);
                        }}
                      >
                        View Details
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  }
                />
              ))}
            </CollapsibleRecordList>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No resolutions filed yet.</p>
              <p className="text-sm mt-2">Use the Writing a Resolution workflow to add your first.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resolution number registry */}
      {unaId && <ResolutionRegistryList unaId={unaId} />}

      {/* Journal */}
      <JournalEntryLogSection unaId={unaId} maxVisible={5} />

      <MeetingRecordDetailModal
        meeting={selectedMeeting}
        open={!!selectedMeeting}
        onOpenChange={(open) => !open && setSelectedMeeting(null)}
        unaId={unaId || ""}
      />
      <ResolutionDetailModal
        resolution={selectedResolution}
        open={!!selectedResolution}
        onOpenChange={(open) => !open && setSelectedResolution(null)}
        unaId={unaId || ""}
      />
    </div>
  );
}
